import React, { useEffect, useState } from 'react';
import { Loader2, UserMinus, CheckCircle2, XCircle, AlertCircle, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import recruitmentApi from '../../utils/agencyRecruitmentApi';

const STATUS_META = {
  active: { label: 'Active', tone: 'emerald' },
  pending_approval: { label: 'Pending', tone: 'amber' },
  removed: { label: 'Removed', tone: 'slate' },
  rejected: { label: 'Rejected', tone: 'rose' },
};

const Roster = () => {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [removeDialog, setRemoveDialog] = useState({ open: false, talent: null });
  const [removeReason, setRemoveReason] = useState('');
  const [canRejoin, setCanRejoin] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async (status) => {
    setLoading(true);
    try {
      const r = await recruitmentApi.roster(status || (tab !== 'all' ? tab : undefined));
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error('Failed to load roster');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const approve = async (talent_id) => {
    setBusy(true);
    try {
      await recruitmentApi.patchRoster(talent_id, { action: 'approve' });
      toast.success('Approved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  const reject = async (talent_id, reason) => {
    setBusy(true);
    try {
      await recruitmentApi.patchRoster(talent_id, { action: 'reject', notes: reason || 'Not a fit at this time' });
      toast.success('Rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const onConfirmRemove = async () => {
    if (!removeDialog.talent) return;
    if (!removeReason.trim()) return toast.error('Please provide a reason');
    setBusy(true);
    try {
      await recruitmentApi.removeFromRoster(removeDialog.talent.talent_id, {
        reason: removeReason.trim(), can_rejoin: canRejoin,
      });
      toast.success(`Removed${canRejoin ? ' (rejoin allowed)' : ''}`);
      setRemoveDialog({ open: false, talent: null });
      setRemoveReason('');
      setCanRejoin(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="agency-talent-pool-page">
      <div className="flex items-center gap-3 mb-5">
        <Users className="w-6 h-6 text-cyan-600" />
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Talent Pool</h1>
          <p className="text-sm text-slate-600">Your central workspace for agency-recruited talent, marketplace talent, applicants, favorites, and performance.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="active" data-testid="pool-tab-recruited">Agency-Recruited</TabsTrigger>
          <TabsTrigger value="pending_approval" data-testid="pool-tab-pending">Pending Approvals</TabsTrigger>
          <TabsTrigger value="all" data-testid="pool-tab-all">All</TabsTrigger>
          <TabsTrigger value="removed" data-testid="pool-tab-removed">Removed</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="pool-tab-marketplace" disabled>Marketplace <span className="ml-1 text-[9px] uppercase text-slate-400">soon</span></TabsTrigger>
          <TabsTrigger value="favorites" data-testid="pool-tab-favorites" disabled>Favorites <span className="ml-1 text-[9px] uppercase text-slate-400">soon</span></TabsTrigger>
          <TabsTrigger value="preferred" data-testid="pool-tab-preferred" disabled>Preferred <span className="ml-1 text-[9px] uppercase text-slate-400">soon</span></TabsTrigger>
          <TabsTrigger value="top" data-testid="pool-tab-top" disabled>Top Talent <span className="ml-1 text-[9px] uppercase text-slate-400">soon</span></TabsTrigger>
          <TabsTrigger value="recent" data-testid="pool-tab-recent" disabled>Recently Worked <span className="ml-1 text-[9px] uppercase text-slate-400">soon</span></TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
          ) : rows.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-xl p-12 text-center text-slate-500" data-testid="roster-empty">
              No talent in this tab yet.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.active;
                return (
                  <Card key={r.roster_id} data-testid={`roster-row-${r.talent_id}`}>
                    <CardContent className="py-4 px-4 flex items-center gap-3 flex-wrap">
                      {r.profile_photo_url ? (
                        <img src={r.profile_photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                          {(r.first_name || '?').charAt(0)}{(r.last_name || '').charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{r.first_name} {r.last_name}</span>
                          <Badge className={`bg-${meta.tone}-50 text-${meta.tone}-700 border border-${meta.tone}-200 text-[10px] uppercase`}>{meta.label}</Badge>
                          <span className="text-xs text-slate-500">{r.email}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.city}{r.state ? `, ${r.state}` : ''} ·
                          joined {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : '—'}
                          {r.joined_via ? ` · via ${r.joined_via.replace('_', ' ')}` : ''}
                          {r.recruitment_slug ? ` · /${r.recruitment_slug}` : ''}
                        </div>
                        {r.removed_reason && (
                          <div className="text-xs text-slate-500 italic mt-0.5">Reason: {r.removed_reason}{r.can_rejoin === false && ' · rejoin blocked'}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {r.status === 'pending_approval' && (
                          <>
                            <Button size="sm" onClick={() => approve(r.talent_id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${r.talent_id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => reject(r.talent_id)} disabled={busy} data-testid={`reject-${r.talent_id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {r.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => setRemoveDialog({ open: true, talent: r })} data-testid={`remove-${r.talent_id}`}>
                            <UserMinus className="w-3.5 h-3.5 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={removeDialog.open} onOpenChange={(o) => !o && setRemoveDialog({ open: false, talent: null })}>
        <DialogContent className="max-w-md" data-testid="remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove from team</DialogTitle>
            <DialogDescription>
              {removeDialog.talent && `${removeDialog.talent.first_name} ${removeDialog.talent.last_name} will be notified.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason (shared with talent)</Label>
              <Textarea rows={3} value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} placeholder="e.g. No longer working with us" data-testid="remove-reason-input" />
            </div>
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <div>
                <div className="font-medium text-sm">Allow rejoin</div>
                <div className="text-xs text-slate-500">When OFF, this talent cannot apply to your agency again.</div>
              </div>
              <Switch checked={canRejoin} onCheckedChange={setCanRejoin} data-testid="remove-canrejoin-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialog({ open: false, talent: null })} disabled={busy}>Cancel</Button>
            <Button onClick={onConfirmRemove} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="remove-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Roster;
